import { useEffect, useState } from 'react'
import type { Tier } from '../../lib/api'
import { adminApi, type UserSort } from '../../lib/adminApi'
import { adminHref } from '../../lib/adminRoute'
import { UserPanel } from './UserPanel'
import {
  DataTable,
  ErrorNote,
  Filter,
  Loading,
  Pager,
  Pill,
  ago,
  fmt,
  useAsync,
} from './parts'

const LIMIT = 25

export function Users({
  isSuperAdmin,
  selfId,
  initialUserId,
}: {
  isSuperAdmin: boolean
  selfId: string
  initialUserId?: string
}) {
  const [q, setQ] = useState('')
  const [tier, setTier] = useState<Tier | ''>('')
  const [adminsOnly, setAdminsOnly] = useState(false)
  const [suspended, setSuspended] = useState<'' | 'yes' | 'no'>('')
  const [sort, setSort] = useState<UserSort>('created_desc')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<string | null>(initialUserId ?? null)

  const key = `users:${q}:${tier}:${adminsOnly}:${suspended}:${sort}:${offset}`
  const { data, error, loading, reload } = useAsync(
    () =>
      adminApi.users({
        q: q || undefined,
        tier: tier || undefined,
        admins_only: adminsOnly || undefined,
        suspended: suspended === '' ? undefined : suspended === 'yes',
        sort,
        limit: LIMIT,
        offset,
      }),
    key,
  )

  // replaceState, not navigate(): selecting a row has to make the URL shareable
  // without pushing a history entry per click, and without re-rendering the
  // route — which would remount this list and drop the filters you set to find
  // the account in the first place.
  useEffect(() => {
    const href = selected ? adminHref('users', selected) : adminHref('users')
    if (window.location.pathname !== href) window.history.replaceState({}, '', href)
  }, [selected])

  /** Any filter change invalidates the current page — staying on page 3 of a
   * result set that no longer has three pages shows an empty table. */
  const change = <T,>(set: (v: T) => void) => (v: T) => {
    set(v)
    setOffset(0)
  }

  return (
    <div className={`staff-split${selected ? ' is-open' : ''}`}>
      <div className="staff-split__main">
        <div className="staff-filters">
          <Filter label="Search">
            <input
              className="staff-input"
              value={q}
              placeholder="email or name"
              onChange={(e) => change(setQ)(e.target.value)}
            />
          </Filter>
          <Filter label="Plan">
            <select
              className="staff-input"
              value={tier}
              onChange={(e) => change(setTier)(e.target.value as Tier | '')}
            >
              <option value="">any</option>
              <option value="free">free</option>
              <option value="pro">pro</option>
              <option value="family">family</option>
            </select>
          </Filter>
          <Filter label="Suspended">
            <select
              className="staff-input"
              value={suspended}
              onChange={(e) => change(setSuspended)(e.target.value as '' | 'yes' | 'no')}
            >
              <option value="">any</option>
              <option value="yes">suspended</option>
              <option value="no">active</option>
            </select>
          </Filter>
          <Filter label="Sort">
            <select
              className="staff-input"
              value={sort}
              onChange={(e) => change(setSort)(e.target.value as UserSort)}
            >
              <option value="created_desc">newest</option>
              <option value="created_asc">oldest</option>
              <option value="seen_desc">last seen</option>
              <option value="reminders_desc">most reminders</option>
              <option value="email_asc">email A–Z</option>
              <option value="name_asc">name A–Z</option>
            </select>
          </Filter>
          <label className="staff-check">
            <input
              type="checkbox"
              checked={adminsOnly}
              onChange={(e) => change(setAdminsOnly)(e.target.checked)}
            />
            Staff only
          </label>
        </div>

        {error && <ErrorNote message={error} />}
        {loading && !data && <Loading what="accounts" />}

        {data && (
          <>
            <DataTable
              rows={data.items}
              rowId={(r) => r.id}
              activeId={selected}
              onRow={(r) => setSelected(r.id)}
              empty="No accounts match those filters."
              columns={[
                {
                  key: 'who',
                  head: 'Account',
                  render: (r) => (
                    <div className="staff-who">
                      <strong>{r.display_name}</strong>
                      <span>{r.email}</span>
                    </div>
                  ),
                },
                {
                  key: 'tier',
                  head: 'Plan',
                  render: (r) => (
                    <Pill tone={r.tier === 'free' ? 'slate' : 'teal'}>{r.tier}</Pill>
                  ),
                },
                {
                  key: 'flags',
                  head: 'Flags',
                  render: (r) => (
                    <>
                      {r.is_super_admin && <Pill tone="amber">super</Pill>}
                      {r.is_admin && !r.is_super_admin && <Pill tone="amber">admin</Pill>}
                      {r.suspended_at && <Pill tone="red">suspended</Pill>}
                    </>
                  ),
                },
                {
                  key: 'rem',
                  head: 'Reminders',
                  numeric: true,
                  render: (r) => `${fmt(r.reminders_active)} / ${fmt(r.reminders_total)}`,
                },
                {
                  key: 'push',
                  head: 'Devices',
                  numeric: true,
                  render: (r) => fmt(r.push_sub_count),
                },
                { key: 'seen', head: 'Last seen', render: (r) => ago(r.last_seen_at) },
                { key: 'made', head: 'Joined', render: (r) => ago(r.created_at) },
              ]}
            />
            <Pager
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onOffset={setOffset}
            />
          </>
        )}
      </div>

      {selected && (
        <UserPanel
          userId={selected}
          isSuperAdmin={isSuperAdmin}
          selfId={selfId}
          onClose={() => setSelected(null)}
          onChanged={reload}
        />
      )}
    </div>
  )
}
